/**
 * Image caching utility for Google Maps static images
 * Caches images for 24 hours to reduce API requests
 */

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const DB_NAME = 'maps-image-cache';
const DB_VERSION = 1;
const STORE_NAME = 'images';

interface CachedImage {
    url: string;
    blob: Blob;
    timestamp: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
    if (dbPromise) {
        return dbPromise;
    }

    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            reject(new Error('Failed to open IndexedDB'));
        };

        request.onsuccess = () => {
            resolve(request.result);
        };

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
                store.createIndex('timestamp', 'timestamp', { unique: false });
            }
        };
    });

    return dbPromise;
}

async function getCachedImage(url: string): Promise<CachedImage | null> {
    try {
        const db = await openDatabase();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(url);

            request.onsuccess = () => {
                const result = request.result;
                if (result) {
                    const age = Date.now() - result.timestamp;
                    if (age < CACHE_DURATION) {
                        resolve(result);
                    } else {
                        // Cache expired, delete it
                        deleteCachedImage(url).catch(console.error);
                        resolve(null);
                    }
                } else {
                    resolve(null);
                }
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    } catch (error) {
        console.error('Error getting cached image:', error);
        return null;
    }
}

async function setCachedImage(url: string, blob: Blob): Promise<void> {
    try {
        const db = await openDatabase();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const data: CachedImage = {
                url,
                blob,
                timestamp: Date.now(),
            };
            const request = store.put(data);

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    } catch (error) {
        console.error('Error caching image:', error);
    }
}

async function deleteCachedImage(url: string): Promise<void> {
    try {
        const db = await openDatabase();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.delete(url);

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    } catch (error) {
        console.error('Error deleting cached image:', error);
    }
}

/**
 * Clean up expired cache entries (older than 24 hours)
 */
export async function cleanupExpiredCache(): Promise<void> {
    try {
        const db = await openDatabase();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('timestamp');
            const cutoffTime = Date.now() - CACHE_DURATION;
            const request = index.openCursor(IDBKeyRange.upperBound(cutoffTime));

            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                } else {
                    resolve();
                }
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    } catch (error) {
        console.error('Error cleaning up expired cache:', error);
    }
}

/**
 * Get a cached image URL or fetch and cache a new one
 * @param imageUrl The URL of the image to fetch/cache
 * @returns A blob URL that can be used as an img src
 */
export async function getCachedImageUrl(imageUrl: string): Promise<string> {
    // Check cache first
    const cached = await getCachedImage(imageUrl);
    if (cached) {
        return URL.createObjectURL(cached.blob);
    }

    // Fetch and cache
    try {
        const response = await fetch(imageUrl);
        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.statusText}`);
        }

        const blob = await response.blob();
        await setCachedImage(imageUrl, blob);

        return URL.createObjectURL(blob);
    } catch (error) {
        console.error('Error fetching image:', error);
        // Fallback to original URL if fetch fails
        return imageUrl;
    }
}

/**
 * Preload and cache an image
 * @param imageUrl The URL of the image to preload
 */
export async function preloadImage(imageUrl: string): Promise<void> {
    try {
        await getCachedImageUrl(imageUrl);
    } catch (error) {
        console.error('Error preloading image:', error);
    }
}

// Clean up expired cache on load (runs once)
if (typeof window !== 'undefined') {
    cleanupExpiredCache().catch(console.error);
}
